/**
 * Helpers for resolving stop ids to keys used in the stop-lines mapping.
 */

import { resolveGtfsStationKey } from "../tools/gtfsStopId.tools";

/** Options for resolving a prefixed stop id to a stop-lines mapping key. */
export interface StopLinesLookupOptions {

    /** GTFS source prefix when available (e.g. `tpg`, `ch_sbb`). */
    "gtfsSource"?: string | null;

    /** Parent station id from a child stop DTO when available. */
    "parentStation"?: string | null;
}

/**
 * Guesses the GTFS source from the first `_`-delimited segment of a stop id when
 * no explicit hint is provided (dataset names never contain `_` in practice).
 *
 * @param stopId Prefixed stop id.
 * @returns First `_`-delimited segment, or `null` when absent.
 */
const guessGtfsSource = ( stopId: string ): string | null => {
    const separatorIndex = stopId.indexOf( "_" );

    return separatorIndex > 0 ? stopId.slice( 0, separatorIndex ) : null;
};

/**
 * Resolves a prefixed stop id to the key used in `StopLinesMappingResponseDto.lines`.
 *
 * The declared `parentStation` hint wins when provided (a child stop groups under its
 * station); otherwise the stop id itself is normalized. Both paths share the backend
 * {@link resolveGtfsStationKey} rule chain, so Swiss sloid URNs, SNCF `StopPoint:`/`StopArea:`
 * ids, platform suffixes, and namespaced feed ids resolve the same way on both sides.
 *
 * @param stopId Prefixed stop id from the spatial index or geocoder.
 * @param options Optional GTFS source and parent station hints.
 * @returns Mapping key, or an empty string when `stopId` is blank.
 */
export const resolveStopLinesMappingKey = (
    stopId: string,
    options: StopLinesLookupOptions = {},
): string => {
    const trimmedStopId = stopId.trim();

    if ( trimmedStopId === "" ) {
        return "";
    }

    const parentStation = options.parentStation?.trim() ?? "";

    if ( parentStation !== "" ) {
        return resolveGtfsStationKey( parentStation, options.gtfsSource ?? guessGtfsSource( parentStation ) );
    }

    return resolveGtfsStationKey( trimmedStopId, options.gtfsSource ?? guessGtfsSource( trimmedStopId ) );
};
